import { ConfigUserService } from './config-user.service';
import { inject, Injectable } from '@angular/core';

import { Platform, AlertController } from '@ionic/angular/standalone';

import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';

import { EasyDebugDecorator } from '../../app/decorators/easy-debug.decorator';

import { LaunchReview } from '@awesome-cordova-plugins/launch-review/ngx';
import { Market } from '@awesome-cordova-plugins/market/ngx';
import { environment } from '@app/env';
import { Session, Student } from '@app/app.models';
import { NavigationService } from './navigation.service';
import { GamificationService } from './gamification.service';

type RatingTrigger = 'seriesSuccess' | 'examBooked' | 'lessonRated5';

@Injectable({
  providedIn: 'root',
})
@EasyDebugDecorator
export class AppRatingService {
  private readonly alertCtrl = inject(AlertController);
  private readonly configUserService = inject(ConfigUserService);
  private readonly platform = inject(Platform);
  private readonly navigationService = inject(NavigationService);
  private readonly market = inject(Market);
  private readonly launchReview = inject(LaunchReview);
  private readonly gamificationService = inject(GamificationService);

  private readonly MAX_DIALOG_WAIT_TIME_IOS = 5 * 1000;
  private readonly PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
  private readonly MIN_SESSIONS_BY_TRIGGER: Record<RatingTrigger, number> = {
    seriesSuccess: 3,
    examBooked: 0,
    lessonRated5: 0,
  };
  private readonly MAX_PROMPTS_BY_TRIGGER: Record<RatingTrigger, number> = {
    seriesSuccess: 2,
    examBooked: 1,
    lessonRated5: 2,
  };
  private isCheckingRating = false;
  private readonly rating = new BehaviorSubject<number>(0);
  readonly rating$ = this.rating.asObservable();
  launched = false;

  displayRatingSlider(value: number): void {
    this.rating.next(value);
  }

  async tryShowOnSeriesSuccess(student: Student, sessions: Array<Session>): Promise<void> {
    await this.tryShowRating('seriesSuccess', student, sessions);
  }

  async tryShowOnExamBooked(student: Student): Promise<void> {
    await this.tryShowRating('examBooked', student, []);
  }

  async tryShowOnLessonRated5(student: Student): Promise<void> {
    await this.tryShowRating('lessonRated5', student, []);
  }

  private async tryShowRating(trigger: RatingTrigger, student: Student, sessions: Array<Session>): Promise<void> {
    if (this.isCheckingRating) return;
    this.isCheckingRating = true;
    try {
      if (!this.platform.is('cordova')) return;

      const minSessions = this.MIN_SESSIONS_BY_TRIGGER[trigger];
      if (minSessions > 0) {
        const usableSessions = sessions.filter(
          (elt) => elt.mode !== 'Cdr::ErrorSession' && elt.type !== 'Discovery' && elt.state !== 'pending'
        );
        if (usableSessions.length < minSessions) return;
      }

      const config = await this.configUserService.getUserConfig(student);
      if (!config || config.appRatingDone === true) return;

      const lastPromptDate: number | null = config.appRatingLastPromptDate ?? null;
      if (lastPromptDate !== null && Date.now() - lastPromptDate < this.PROMPT_COOLDOWN_MS) return;

      const counts: Record<RatingTrigger, number> = {
        seriesSuccess: 0,
        examBooked: 0,
        lessonRated5: 0,
        ...(config.appRatingPromptCountByTrigger ?? {}),
      };
      if (counts[trigger] >= this.MAX_PROMPTS_BY_TRIGGER[trigger]) return;

      config.appRatingLastPromptDate = Date.now();
      config.appRatingPromptCountByTrigger = { ...counts, [trigger]: counts[trigger] + 1 };
      await this.configUserService.setUserConfig(student, config);
      this.launchAppRating(student);
    } finally {
      this.isCheckingRating = false;
    }
  }

  private async markAsRated(student: Student): Promise<void> {
    const config = await this.configUserService.getUserConfig(student);
    if (!config || config.appRatingDone === true) return;
    config.appRatingDone = true;
    await this.configUserService.setUserConfig(student, config);
  }

  async launchAppRating(student: Student): Promise<void> {
    const confirm = await this.alertCtrl.create({
      header: `Que penses-tu d'En Voiture Simone ?`,
      message: `Ton avis nous intéresse ! Choisis l'une des réponses ci-dessous pour nous aider à améliorer l'application.`,
      buttons: [
        {
          text: `J'adore`,
          handler: () => {
            this.rate(student);
          },
        },
        {
          text: 'Je suis déçu-e',
          handler: () => {
            this.rating.next(3);
            this.launched = true;
            this.navigationService.open('rating', { forceRoot: true });
          },
        },
        {
          text: 'Plus Tard',
        },
      ],
      mode: 'ios',
    });
    confirm.present();
  }

  rate(student: Student): void {
    if (!this.launchReview.isRatingSupported() || !this.platform.is('ios')) {
      this.openStoreViaPlugin(student);
      return;
    }

    let ratingTimerId: ReturnType<typeof setTimeout> | undefined;
    let sub: Subscription | undefined;
    let completed = false;

    const finalize = (action: () => void): void => {
      if (completed) return;
      completed = true;
      clearTimeout(ratingTimerId);
      sub?.unsubscribe();
      action();
    };

    sub = this.launchReview.rating().subscribe({
      next: (status) => {
        if (status === 'requested') {
          ratingTimerId = setTimeout(() => {
            finalize(() => this.openStoreViaPlugin(student));
          }, this.MAX_DIALOG_WAIT_TIME_IOS);
        } else if (status === 'shown' || status === 'dismissed') {
          finalize(() => {
            void this.markAsRated(student);
            this.validateRating(student);
          });
        }
      },
      error: () => {
        finalize(() => this.openStoreViaPlugin(student));
      },
    });
  }

  openStoreViaPlugin(student: Student): void {
    void this.markAsRated(student);
    this.validateRating(student);

    if (this.platform.is('android')) {
      this.market.open(environment.packageName);
    }
    if (this.platform.is('ios')) {
      this.market.open(environment.iosAppId);
    }
  }

  validateRating(student: Student): void {
    if (!student.isGuest()) {
      firstValueFrom(this.gamificationService.validateBadge('rate_app')).catch(() => {
        /* best-effort */
      });
    } else {
      this.gamificationService.unlockBadgeInStorage('rate_app');
    }
  }
}
